<template>
	<div>
		<div class="offer__presentation" @click.prevent.stop="toggleBuyBoxInfoModal(true)">
			<div class="offer__presentation__icons">
				<p class="offer__presentation__icons__subtitles">CC</p>
				<p v-if="presentationTypeLabel" class="offer__presentation__icons__quality">
					{{ presentationTypeLabel }}
				</p>
				<p v-if="ageRating" class="offer__presentation__icons__age">
					<svg xmlns="http://www.w3.org/2000/svg" width="15" height="12" viewBox="0 0 15 12" fill="none">
						<circle cx="6" cy="6" r="5.5" stroke="#5AA152" />
						<path d="M3.5 5.5L7 8.5L14 1" stroke="white" stroke-linecap="round" stroke-linejoin="round" />
					</svg>
					{{ ageRating }}
				</p>
				<p v-if="dolbyTechnology" class="offer__presentation__icons__dolby">
					<svg
						xmlns="http://www.w3.org/2000/svg"
						width="20"
						height="16"
						fill="#fff"
						role="img"
						viewBox="0 3.65 24 16.7"
					>
						<path
							d="M24 20.352V3.648H0v16.704h24zM18.433 5.806h2.736v12.387h-2.736c-2.839 0-5.214-2.767-5.214-6.194s2.375-6.193 5.214-6.193zm-15.602 0h2.736c2.839 0 5.214 2.767 5.214 6.194s-2.374 6.194-5.214 6.194H2.831V5.806z"
						></path>
					</svg>
				</p>
			</div>
			<p class="offer__presentation__info">
				<strong v-if="showText">{{ showText }} - </strong>
				<span v-if="runtimeMins">{{ runtimeMins }}</span>
				<span v-if="audioLanguages"> - {{ audioLanguages }}</span>
			</p>
		</div>
		<BuyBoxInfoModal
			:displayBuyBoxInfoModal="displayBuyBoxInfoModal"
			:title="title"
			:offer="offer"
			@close="toggleBuyBoxInfoModal($event)"
		/>
	</div>
</template>

<script setup lang="ts">
import { AudioTechnology, PresentationType, VideoTechnology } from '@/@types/graphql-types';
import { BuyboxRegularOffer } from '@/features/buybox/types/types';
import { getTranslatableForShowOfferGraphql } from '@/helpers/offer-helper';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { TitleDetail } from '@/interfaces/title-details-graphql';
import { computed, ref } from 'vue';

const BuyBoxInfoModal = defineAsyncComponent(() => import('@/features/buybox/components/list/BuyBoxInfoModal.vue'));

const props = defineProps<{
	offer: BuyboxRegularOffer;
	title: TitleDetail;
}>();

const { t, te } = useI18n();

const displayBuyBoxInfoModal = ref<boolean>(false);

const buyboxOffer = computed(() => {
	return props.offer;
});

const audioLanguages = computed(() => {
	if (!buyboxOffer.value?.audioLanguages) return false;
	return languageArrayToString(props?.offer?.audioLanguages);
});

const presentationTypeLabel = computed(() => {
	switch (buyboxOffer.value?.presentationType) {
		case PresentationType.Hd:
			return 'HD';
		case PresentationType['4K']:
			return '4K';
		case PresentationType.Dvd:
			return 'DVD';
		case PresentationType.Bluray:
			return 'Blu-Ray';
		default:
			return '';
	}
});

const ageRating = computed(() => {
	if (props.title.__typename !== 'Season') {
		return props.title.content.ageCertification;
	}
	return null;
});

const dolbyTechnology = computed(() => {
	return (
		buyboxOffer.value?.videoTechnology.includes(VideoTechnology.DolbyVision) ||
		buyboxOffer.value?.audioTechnology.includes(AudioTechnology.Dolby_5Point_1) ||
		buyboxOffer.value?.audioTechnology.includes(AudioTechnology.DolbyAtmos)
	);
});

const showText = computed(() => getTranslatableForShowOfferGraphql(buyboxOffer.value, props.title.objectType));

const runtimeMins = computed(() => (props.title?.content?.runtime ? `${props.title.content.runtime}min` : null));

function languageArrayToString(languageArray: string[]) {
	return languageArray
		.map(language => {
			const key = `WEBAPP_LANGUAGE_${language.toUpperCase()}`;
			if (te(key)) {
				return t(key);
			}
			return null;
		})
		.filter(Boolean)
		.join(', ');
}

function toggleBuyBoxInfoModal(display: boolean) {
	displayBuyBoxInfoModal.value = display;

	if (displayBuyBoxInfoModal.value) {
		TrackingHelper.trackEvent('userinteraction', { action: 'more_info_clicked', label: 'buybox' });
	}
}
</script>

<style lang="scss" scoped>
@import '~@/styles/variables';
@import '@/styles/mixins.scss';

.content-wrapper {
	flex: 1;
	display: flex;
	justify-content: space-between;

	@media (max-width: 1024px) {
		flex-direction: column;
		justify-content: flex-start;
		width: 50%;
	}
	&--left {
		align-items: flex-start;

		@media (max-width: 1345px) {
			justify-content: flex-start;
		}
	}

	&--right {
		flex: 1;
		align-items: flex-start;
		max-width: 50%;

		@media (max-width: 1024px) {
			flex: 0 1 auto;
			align-items: flex-end;
			max-width: none;
		}
	}
}

.offer {
	&__presentation {
		display: flex;
		flex-direction: column;
		width: 100%;
		margin-top: 6px;

		@media (max-width: 1024px) {
			justify-content: flex-end;
			gap: 4px;
			margin-top: 26px;
		}

		&__icons {
			display: flex;
			align-items: center;
			gap: 4px;
			flex-grow: 1;
			margin-bottom: 4px;
			margin-top: 1px;
			max-height: 12px;

			@media (max-width: 1024px) {
				flex-grow: 0;
				justify-content: flex-start;
			}

			&__subtitles,
			&__quality {
				color: #c6c8cd;
				font-size: 11px;
				letter-spacing: 0.18px;
				border-radius: 2px;
				border: 1px solid #c6c8cd;
				padding: 0 2px;
				margin: 0;
				line-height: 16px;
			}

			&__quality {
				color: #fbc500;
				border: 1px solid #fbc500;
			}

			&__age,
			&__dolby {
				display: flex;
				align-items: center;
				gap: 1px;
				color: #fff;
				font-size: 11px;
				line-height: 16px;
				letter-spacing: 0.18px;
				margin: 0;
				white-space: nowrap;
				text-overflow: ellipsis;
			}
		}

		&__info {
			margin: 0;
			color: #c6c8cd;
			font-size: 14px;
			line-height: 24px;
			letter-spacing: 0.21px;
			overflow: hidden;
			text-overflow: ellipsis;

			// OVER_PROVIDER
			// if full implementation happens, this component can be used for ListOffers and ListPromotionRow > PromotionRowOfferPresentation
			// this would be the only place where we need to make changes
			@include multi-line-clamp(1);
			// OVER_PROVIDER
		}
	}

	&__label {
		@media (max-width: 1024px) {
			margin-bottom: 10px;
			text-align: end;
		}

		&__text {
			margin: 0;
			color: #fff;
			font-size: 14px;
			font-weight: 900;
			line-height: 16px;
			letter-spacing: 0.21px;
			margin-bottom: 4px;

			:deep(.disclaimer-button) {
				margin-top: -4px;
				vertical-align: middle;
			}
		}

		&__free-trial {
			margin: 0;
			color: #ffe894;
			font-size: 14px;
			font-weight: 900;
			line-height: 16px;
			letter-spacing: 0.21px;
			margin-bottom: 4px;
		}

		&__price {
			margin: 0;
			color: #c6c8cd;
			font-size: 14px;
			font-weight: 400;
			line-height: 24px;
			letter-spacing: 0.21px;

			&--breakdown {
				@media (max-width: 1024px) {
					margin-bottom: -24px;
				}
			}
		}
	}

	&__cta {
		margin-inline-start: 8px;

		height: 40px;
		width: fit-content;
		min-width: 166px;
		padding: 8px 16px;
		border-radius: 8px;
		border: none;
		background: #303740;
		color: #c6c8cd;
		font-size: 16px;
		font-weight: 900;
		letter-spacing: 0.24px;
		cursor: pointer;

		white-space: nowrap;
		text-overflow: ellipsis;
		overflow: hidden;

		&:hover {
			background: #303740cb;
		}

		@media (max-width: 1100px) {
			min-width: 110px;
		}

		@media (max-width: 1050px) {
			min-width: 166px;
		}

		@media (max-width: 1024px) {
			min-width: 166px;
			width: fit-content;
			padding: 0 16px;
			margin-top: 26px;
			flex: 0 1 auto;
			margin-inline-start: 0;
		}

		&--highlight {
			background: #fbc500;
			color: #000;

			&:hover {
				background: #fbc500b1;
			}
		}

		&__text {
			white-space: nowrap;
			overflow: hidden;
			text-overflow: ellipsis;
		}
	}
}
.picture-wrapper {
	&:has(.ribbon) {
		position: relative;

		.picture-element {
			position: relative;
			z-index: 2;
		}
	}

	min-width: 120px;
	min-height: 40px;
	margin-bottom: auto;
	margin-inline-end: 48px;

	@media (max-width: 1345px) {
		min-width: 80px;
	}

	@media (max-width: 1024px) {
		margin-inline-end: 16px;
		margin-bottom: 10px;
	}
}

.play-icon {
	margin-inline-end: 8px;
}

.leaving-banner-container {
	padding-top: 8px;
	background-color: inherit;
}

.leaving-banner {
	display: flex;
	padding: 8px 16px;
	background: rgba(250, 0, 42, 0.08);
	color: #fa002a;
	font-size: 14px;
	font-style: normal;
	font-weight: 400;
	line-height: 16px;
	letter-spacing: 0.21px;
	align-items: center;
	span {
		margin-inline-start: 8px;
	}
}

.transparent {
	color: transparent;
}

.price-fast {
	font-size: 14px;
	line-height: 18px;
	padding-top: 6px;
}

.ribbon {
	position: absolute;
	width: 100%;
	max-width: 80px;
	text-align: center;
	background-color: $jw-color-lightgray;
	color: $jw-color-darkgray;
	font-size: 9px;
	font-weight: 700;
	padding-inline: 4px;
	padding-top: 8px;
	border-radius: 0 0 0 5px;
	z-index: 0;
	left: 0;
	bottom: -5px;
	border-bottom-left-radius: 8px;
	border-bottom-right-radius: 8px;
}
</style>
